/**
 * The recent files list (§10).
 *
 * Shown on the empty state, under the recovery offer. The order matters: work
 * that is at risk of being lost comes before work that is merely convenient to
 * reach.
 *
 * Entries are verified against the store before they are drawn, so a file that
 * has been moved or deleted disappears rather than failing on click.
 */
import { useCallback, useEffect, useState } from 'react';
import { FORMATS_BY_ID, formatFileSize, formatRelativeTime } from '@moduviewer/core';
import type { FileStore } from '@moduviewer/core';
import { Icon, iconForFormat } from '@moduviewer/design/icon';
import { clearRecentFiles, forgetRecentFile, pruneMissing } from '../lib/recent-files.js';
import type { RecentFile } from '../lib/recent-files.js';
import { isFavourite, toggleFavourite } from '../lib/favourites.js';

export interface RecentFilesProps {
  readonly store: FileStore;
  /** False in a browser, where a handle cannot outlive the tab. */
  readonly reopenable: boolean;
  readonly onOpen: (file: RecentFile) => void;
  /** Called after a pin toggle so the favourites list above can refresh. */
  readonly onFavouritesChanged?: () => void;
}

export function RecentFiles({
  store, reopenable, onOpen, onFavouritesChanged,
}: RecentFilesProps): React.JSX.Element | null {
  const [files, setFiles] = useState<RecentFile[] | null>(null);
  // A tick to redraw the stars after a toggle without re-querying the store.
  const [, bump] = useState(0);

  const load = useCallback(async () => {
    if (!reopenable) { setFiles([]); return; }
    try {
      setFiles(await pruneMissing(store));
    } catch {
      // A store that cannot be queried is not a reason to block the app; the
      // user came here to open a file (§34).
      setFiles([]);
    }
  }, [store, reopenable]);

  useEffect(() => { void load(); }, [load]);

  if (files === null || files.length === 0) return null;

  return (
    <section className="recent" aria-labelledby="recent-title">
      <div className="recent__head">
        <h2 className="recent__title" id="recent-title">최근 파일</h2>
        <button
          className="btn btn--ghost"
          onClick={() => { clearRecentFiles(); void load(); }}
        >
          목록 지우기
        </button>
      </div>

      <ul className="recent__list">
        {files.map((file) => (
          <li key={file.handle} className="recent-item">
            <button
              className="recent-item__open"
              onClick={() => onOpen(file)}
              title={file.handle}
            >
              <Icon
                name={file.formatId
                  ? iconForFormat(file.formatId, FORMATS_BY_ID.get(file.formatId)?.family)
                  : 'file'}
                size={18}
                className="recent-item__icon"
              />
              <span className="recent-item__text">
                <span className="recent-item__name truncate">{file.displayName}</span>
                <span className="recent-item__meta">
                  <span>{formatRelativeTime('ko', new Date(file.openedAtMs), new Date())}</span>
                  <span aria-hidden="true">·</span>
                  <span className="tnum">{formatFileSize('ko', file.sizeBytes)}</span>
                </span>
              </span>
            </button>
            <button
              className="btn btn--icon tip"
              data-tip={isFavourite(file.handle) ? '즐겨찾기에서 제거' : '즐겨찾기에 추가'}
              aria-label={`${file.displayName} ${isFavourite(file.handle) ? '즐겨찾기에서 제거' : '즐겨찾기에 추가'}`}
              aria-pressed={isFavourite(file.handle)}
              onClick={() => {
                // A recent entry is by definition reopenable and not sensitive
                // — it would not be in this list otherwise — so pinning it is
                // always allowed.
                toggleFavourite({
                  handle: file.handle,
                  displayName: file.displayName,
                  formatId: file.formatId,
                  sizeBytes: file.sizeBytes,
                  sensitive: false,
                  reopenable: true,
                }, Date.now());
                bump((n) => n + 1);
                onFavouritesChanged?.();
              }}
            >
              <Icon name={isFavourite(file.handle) ? 'star-filled' : 'star'} size={15} />
            </button>
            <button
              className="btn btn--icon tip"
              data-tip="목록에서 제거"
              aria-label={`${file.displayName} 목록에서 제거`}
              onClick={() => { forgetRecentFile(file.handle); void load(); }}
            >
              <Icon name="close" size={14} />
            </button>
          </li>
        ))}
      </ul>

      <p className="recent__note">
        {/* §29, stated where it applies rather than buried in a policy. */}
        이 목록은 이 기기에만 저장되며, 인증 정보가 들어 있을 수 있는 파일은 기록하지 않습니다.
      </p>
    </section>
  );
}
